/**
 * Algae — dense voxel seagrass on the lagoon sand, that gets shouldered aside.
 *
 * WHERE IT GROWS is not a scatter, it is a bed: sand rather than rock, and only in the depth
 * band with light enough to grow and calm enough not to be torn out. That puts it squarely in
 * the lagoon, which was the emptiest water in the game.
 *
 * HOW IT MOVES has two halves, and the second is the point.
 *
 *   sway         a travelling wave over WORLD position, so blades lean together in ranks. A
 *                per-blade phase gives a field of independent wobbles; a wave gives a surface,
 *                and moving water moves grass as a surface.
 *
 *   disturbance  creatures push blades aside and press them down. The naive version — bend by
 *                what is nearby right now — snaps every blade upright the instant the animal
 *                clears it, which reads as a bug rather than as grass. So each creature
 *                arrives as a short TRAIL of recent positions, each weaker than the last, and
 *                a blade takes the STRONGEST of them. The trail ages out over about half a
 *                second, and that is what buys recovery: the bed closes behind a shark instead
 *                of shutting like a door, and there is a visible swathe where it went through.
 *
 * BIOLUMINESCENCE falls out of that for almost nothing. The shader already knows how hard
 * each stipe is being pressed and by whom, and the wake already fades over half a second — so
 * the glow is that same value, gated on darkness. Swim through a bed at night and it lights
 * up behind you and goes out again on its own. There is no particle system, no extra pass and
 * no state: the light IS the disturbance, which is also what it is in the sea.
 *
 * Nothing is simulated per blade and nothing is read back. A bed of several thousand blades is
 * one draw call and two small uniform arrays.
 *
 * WHY THE WINDOW IS SMALL
 * Dense grass is expensive and there is no way around it, so the bed is drawn only as far as
 * it can actually be seen and faded to nothing at the boundary. Blades shrinking to zero is
 * far less noticeable than blades popping, and it costs one multiply.
 */
import * as THREE from 'three';
import { CAMERA, WATER } from '../data/tuning.ts';
import { hashInts } from '../core/Rng.ts';
import { bedHeight, waterDepth, reefMask } from '../world/Coast.ts';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';
import { QUALITY } from '../data/quality.ts';
import type { Disturber } from '../world/Wake.ts';

/** creature trail samples the shader will consider */
export const MAX_DISTURBERS = 18;

/* Voxels up a stipe. Tall kelp needs segments or its bend is a dogleg rather than a curve —
   three was right for ankle-high seagrass and is wrong for something you swim into. Seven is
   the point where the curve reads; the cost is paid back by planting far fewer of them, which
   is what a kelp forest is anyway. */
const LEVELS = 6;
/* Live bed extent, u, and where it is centred.

   Sized to the CAMERA's footprint, not the player's surroundings — which is not the same
   rectangle and not even the same centre. The rig sits astern and pitched 62 degrees down, so
   the ground it can see runs from about 16 u behind the shark to 36 u in front, and is far
   wider than it is deep. A window centred on the player fades kelp out at 36 u in ALL
   directions, which puts the boundary squarely on screen ahead of you. */
const WIN_X = 180 * QUALITY.algaeWindow;
const WIN_Z = 100 * QUALITY.algaeWindow;
/** the bed is centred this far up-screen of the player, toward the middle of the view */
const LOOK_AHEAD = 12;

/**
 * The ground the camera can actually see, derived from the rig rather than guessed.
 *
 * It is a TRAPEZOID: narrow just behind the shark, wide at the far edge, because a pitched
 * camera's frustum meets the ground at an angle. Planting a rectangle that contains it puts
 * seventy per cent of the stipes where nothing can ever look at them — measured, not
 * estimated — and they cost exactly as much as the ones you can see.
 *
 * Derived from the far camera distance and a generous aspect so an ultrawide window never
 * shows an unplanted corner, and so this tracks if the camera is ever re-tuned.
 */
const VIEW = (() => {
  const rad = Math.PI / 180;
  const pitch = CAMERA.pitchDeg * rad;
  // The SCALED distance: a phone's camera stands 20% closer, so it sees less ground, and
  // planting for where the camera used to be would seed a border nobody can look at.
  const d = CAMERA.distanceFar * QUALITY.cameraZoom;
  const camH = d * Math.sin(pitch);
  const camBack = d * Math.cos(pitch);
  const half = CAMERA.fov * rad * 0.5;
  const far = camH / Math.tan(Math.max(0.08, pitch - half)) - camBack;
  const near = -camBack - 6;
  /* Aspect. Generous enough that a wide window is covered, but NOT so generous that the
     trapezoid ends up wider than the planting rectangle — at 2.6 it did, and the cull silently
     never fired along X at all. */
  const hHalf = Math.atan(2.15 * Math.tan(half));
  return {
    near,
    far,
    wideNear: Math.hypot(camH, Math.abs(near + camBack)) * Math.tan(hHalf),
    wideFar: Math.hypot(camH, far + camBack) * Math.tan(hHalf),
  };
})();
/** slack on the trapezoid: enough to cover the fade tail and half a snap */
const VIEW_MARGIN = 14;
/** planting grid pitch, u — the density knob */
const SPACING = 0.62 * QUALITY.algaeSpacing;
/* Where the bed fades, in u from the window centre, per axis — the view is roughly twice as
   wide as it is deep, so one radius cannot serve both. Each must finish well inside its WIN so
   that every stipe the shader asks for has actually been planted: the plant centre is snapped
   and may be half a SNAP from the true one. */
const FADE_X: [number, number] = [64 * QUALITY.algaeWindow, 80 * QUALITY.algaeWindow];
const FADE_Z: [number, number] = [34 * QUALITY.algaeWindow, 46 * QUALITY.algaeWindow];
const CAP = 26000;
/** how much wider a stipe is drawn into the shadow map than it really is */
const SHADOW_FATTEN = 2.4;
/**
 * Coarse rejection cell, u.
 *
 * The density field's shortest wavelength is 55 u, so across four units it is effectively
 * constant — one sample decides a whole block of a hundred-odd planting cells. That is the
 * difference between evaluating a trig-heavy field twenty thousand times per replant and
 * evaluating it sixty times.
 */
const BLOCK = 4.0;
/** how far the player must move before the bed is replanted, u */
const SNAP = 14.0;

function v3(c: THREE.Color): THREE.Vector3 {
  return new THREE.Vector3(c.r, c.g, c.b);
}
function srgb(hex: number): THREE.Color {
  return new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
}
function smooth(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

const VERT = /* glsl */ `
uniform float uTime;
uniform float uFatten;
uniform vec2  uCentre;    // the view's TRUE centre, not the snapped plant centre
uniform vec2  uFadeX;     // where the bed starts and finishes fading along-shore
uniform vec2  uFadeZ;     // and cross-shore, which is a much shorter distance
uniform vec4  uDist[${MAX_DISTURBERS}];   // xyz recent position, w radius
uniform vec3  uDistDrive[${MAX_DISTURBERS}];  // x strength, yz unit heading

attribute float aV;         // 0..1 up the blade, constant across each voxel
attribute vec3  aBase;      // world position of the blade's root
attribute vec4  aBlade;     // yaw, height, phase, tint

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying float vUp;
varying float vTint;
varying float vPressed;

void main() {
  float yaw = aBlade.x;
  float phase = aBlade.z;

  /* THE BOUNDARY FADE LIVES HERE, not in the planting.
     Baking it at plant time anchors it to the snapped centre, so the whole fade ring
     teleports a full snap every time the bed is rebuilt and every blade in it changes height
     in one frame. Evaluated against the viewer's continuous position it is exactly smooth,
     and the plant step is then free to snap as coarsely as it likes. */
  vec2 off = abs(aBase.xz - uCentre);
  float fade = (1.0 - smoothstep(uFadeX.x, uFadeX.y, off.x))
             * (1.0 - smoothstep(uFadeZ.x, uFadeZ.y, off.y));
  float height = aBlade.y * fade;
  if (height < 0.004) {
    // collapse it to a point rather than drawing a sliver
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    vWorldPos = vec3(0.0); vWorldNormal = vec3(0.0, 1.0, 0.0);
    vUp = 0.0; vTint = 0.0; vPressed = 0.0;
    return;
  }
  vTint = aBlade.w;
  vUp = aV;

  float cy = cos(yaw), sy = sin(yaw);
  vec2 dir = vec2(cy, sy);

  /* Sway as a travelling wave over world position. The per-blade phase survives only as a
     small jitter on top, so the ranks are not a perfect grid. */
  // Both rates cut 15% together. They have to move as a pair: the gust is a slow envelope on
  // the wave, so slowing one without the other changes the RATIO between them, and that ratio
  // is what makes the bed surge and ease rather than just oscillate.
  float wave = sin(dot(aBase.xz, vec2(0.21, 0.14)) - uTime * 0.9775 + phase * 0.6);
  float gust = sin(dot(aBase.xz, vec2(-0.06, 0.09)) - uTime * 0.3145) * 0.5 + 0.5;
  /* Fraction of the stipe's own length the tip swings, cut 20%. Applied AS a fraction of
     height, so it stays proportionate however tall a stipe grows — a 7 u stipe leans further
     than a 2 u one in absolute terms, which is what a longer lever actually does. */
  vec2 lean = dir * wave * (0.088 + 0.12 * gust);

  /* Disturbance.
     STRENGTH is the strongest single entry, not the sum: a slow animal's trail samples all sit
     on top of each other, and summing would have it push several times harder than a fast one
     covering the same ground.
     DIRECTION is a weighted blend of all of them, because a hard argmax makes the push vector
     jump the instant one sample overtakes another — the blade flicks from pointing away from
     one trail entry to pointing away from the next. Cubed weights keep the strongest entry
     firmly in charge while letting the direction rotate continuously between them. */
  vec2 acc = vec2(0.0);
  float wsum = 0.0;
  float pressed = 0.0;
  for (int i = 0; i < ${MAX_DISTURBERS}; i++) {
    vec3 drv = uDistDrive[i];
    float amt = drv.x;
    if (amt <= 0.001) continue;
    vec3 d = uDist[i].xyz;
    vec2 off = aBase.xz - d.xz;
    float r = length(off) + 1e-4;
    float radius = uDist[i].w;
    if (r > radius) continue;
    /* Vertical reach. With a stipe several units tall, testing against its MIDPOINT is wrong
       in both directions — an animal at the canopy barely registers and one at the holdfast
       registers as much as one halfway up. So the test is against the nearest point on the
       stipe: anywhere within its vertical span pushes at full strength, and the influence only
       falls away above the canopy or below the base. That gate is most of what makes this read
       as CONTACT rather than as an aura the animal drags around with it. */
    float near = clamp(d.y - aBase.y, 0.0, height);
    float beyond = abs(d.y - (aBase.y + near));
    float over = 1.0 - smoothstep(0.0, 3.0, beyond);

    /* Quintic, and reaching all the way to the centre. smoothstep left a flat-topped plateau
       over the inner third of the radius, so a blade crossed the edge of that plateau and was
       simply switched to maximum — the corner in that curve is what is felt as a snap. This
       has zero first AND second derivative at both ends, so the blade eases into motion and
       eases out of it: no step, no kink, nothing accelerating instantaneously. */
    float t = clamp((radius - r) / radius, 0.0, 1.0);
    float ease = t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
    float f = ease * amt * over;
    if (f <= 0.0) continue;

    /* WHICH WAY. Radially away from the animal is the obvious answer and it is wrong: as a
       creature crosses a blade, "away from it" swings through a full 180 degrees, and it does
       so exactly where the push is strongest. The blade is shoved forward, then whipped back.
       That reversal IS the snap, and no amount of easing the STRENGTH can hide it.

       Water does not do that. A body moving through it drags water along with it and shoulders
       the rest aside, so grass combs the way the animal WENT and splays outward around it. So:
       keep only the across-track part of the radial direction, and add the heading. Ahead of
       the animal the grass leans forward, beside it the grass parts sideways, behind it the
       grass trails — and nowhere in that field does the direction reverse. */
    vec2 radial = off / r;
    vec2 head = drv.yz;
    vec2 splay = radial - head * dot(radial, head);
    vec2 combed = head * 0.9 + splay * 1.25;
    float cl = length(combed);
    // hl is 1 for a moving creature and 0 for a stationary one, which falls back to radial.
    float hl = min(length(head), 1.0);
    vec2 dirI = cl > 1e-4 ? mix(radial, combed / cl, hl) : radial;

    float w = f * f * f;
    acc += dirI * w;
    wsum += w;
    pressed = max(pressed, f);
  }
  /* Averaged, NOT renormalised. Where the entries agree the average is already very close to
     unit length and the push is at full strength; where they disagree it is short, and
     competing pushes partly cancelling is the right answer rather than a singularity. */
  vec2 push = wsum > 1e-6 ? (acc / wsum) * pressed : vec2(0.0);
  vPressed = pressed;

  /* THE BEND IS AN ARC, AND THE STIPE CANNOT STRETCH.

     This used to displace the tip sideways by a flat fraction of the stipe's height while
     leaving its height alone, which is not a bend — it is a lean, and at full push the tip
     travelled TWO stipe-lengths sideways from a stipe one length long. That is why it read as
     rigid: a rod on a hinge, not a plant. The height was then pulled back down by a separate
     squash constant that had nothing to do with how far it had been pushed.

     So instead: bend the stipe through an angle, along a circular arc of CONSTANT LENGTH. A
     station a fraction s up the stipe sits at ((1-cos ks)/k, sin(ks)/k) — lateral and
     vertical, in stipe lengths. Laying it over now COSTS height, exactly as much as the
     geometry says it should, and the curvature is continuous from holdfast to tip instead of
     being a parabola scaled by a number nothing constrains. */
  vec2 drive = lean + push * 1.9;
  float amount = length(drive);
  vec2 bdir = amount > 1e-5 ? drive / amount : vec2(1.0, 0.0);
  /* Total turn root-to-tip, saturating rather than clamped. A clamp makes every push past its
     limit look identical; this keeps growing forever and simply never folds the blade back
     over itself, which is the only thing a circular arc genuinely cannot survive. The 2.0
     inside is chosen so a small angle matches the old lean exactly — ambient sway is unchanged. */
  float theta = 2.4 * (1.0 - exp(-amount * 0.833));
  float k = max(theta, 1e-3);

  /* Placed per CUBE, not per vertex: each voxel is put on the arc and rotated to the stipe's
     local tilt there, so it stays a cube. Curving inside the voxel would be smoother and would
     also throw away the thing that makes this look like the rest of the game. */
  float tilt = k * aV;
  float ca = cos(tilt), sa = sin(tilt);
  vec2 arc = vec2((1.0 - cos(tilt)) / k, sin(tilt) / k);
  float dy = position.y - aV;
  vec2 stem = arc + vec2(dy * sa, dy * ca);

  // What is left of the old squash. The arc now does nearly all of the height loss; this is
  // only the extra flattening of something heavy having actually laid on top of the bed.
  float squash = 1.0 - pressed * 0.22;

  /* uFatten is 1 for the visible pass and larger for the shadow pass. A stipe is about three
     shadow texels wide, which is exactly the width at which a moving object's shadow crawls
     and sparkles as it crosses the grid. Widening it only for the depth render costs one
     multiply, and nobody can tell a soft blade shadow is a little too wide.

     Near-constant, NOT proportional to height: at 20% a six-unit stipe would be a post more
     than a unit thick. Kelp keeps roughly the same stipe whatever it grows to.

     (The variable below was once called 'flat', a RESERVED WORD in GLSL ES. The shader failed
     to compile and the entire bed silently never drew, which looks exactly like a system that
     was never wired up. No backticks in here either: this GLSL lives in a JS template literal.) */
  float thick = (0.085 + height * 0.022) * uFatten;
  vec3 p = position;
  vec2 lateral = vec2(p.x * cy - p.z * sy, p.x * sy + p.z * cy) * thick;
  vec3 grown = aBase + vec3(lateral.x + bdir.x * stem.x * height,
                            stem.y * height * squash,
                            lateral.y + bdir.y * stem.x * height);

  /* The normal rides the same tilt. Decomposed into the component along the bend and the one
     across it, because only the along part rotates — turning the whole vector would twist the
     blade's edges as well as pitch its faces. */
  vec3 n = normal;
  vec3 n0 = vec3(n.x * cy - n.z * sy, n.y, n.x * sy + n.z * cy);
  float nb = dot(n0.xz, bdir);
  vec2 nAcross = n0.xz - bdir * nb;
  float nbT = nb * ca + n0.y * sa;
  vec3 wn = normalize(vec3(nAcross.x + bdir.x * nbT,
                           n0.y * ca - nb * sa,
                           nAcross.y + bdir.y * nbT));

  vec4 wp = modelMatrix * vec4(grown, 1.0);
  vWorldPos = wp.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * wn);
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

const FRAG = /* glsl */ `
uniform vec3  uSunDir;
uniform vec3  uSunColor;
uniform vec3  uAmbientTop;
uniform vec3  uAmbientBottom;
uniform vec3  uFogColor;
uniform float uFogDensity;
uniform vec3  uRoot;
uniform vec3  uTipA;
uniform vec3  uTipB;
uniform float uTime;
uniform float uNight;      // 0 by day, 1 by dark
uniform vec3  uGlow;
uniform sampler2D uShadowMap;
uniform mat4      uShadowMatrix;
uniform float     uShadowStrength;

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying float vUp;
varying float vTint;
varying float vPressed;

float shadowAt(vec3 world, vec3 n, float ndl) {
  // Larger offset than elsewhere: the casters are deliberately fatter than the blades, so a
  // stipe would otherwise sit inside its own widened shadow and self-shadow to black.
  vec4 lp = uShadowMatrix * vec4(world + n * 0.26, 1.0);
  vec3 c = lp.xyz / lp.w;
  c = c * 0.5 + 0.5;
  if (c.x < 0.0 || c.x > 1.0 || c.y < 0.0 || c.y > 1.0 || c.z > 1.0) return 1.0;
  float bias = mix(0.0020, 0.0005, clamp(ndl, 0.0, 1.0));
  float texel = 1.0 / 2048.0;
  float lit = 0.0;
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel,  texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel,  texel)).r);
  lit *= 0.25;
  vec2 e = abs(c.xy - 0.5) * 2.0;
  return mix(1.0, lit, 1.0 - smoothstep(0.80, 1.0, max(e.x, e.y)));
}

void main() {
  vec3 N = normalize(vWorldNormal);
  vec3 L = normalize(uSunDir);

  /* Dark at the root, bright at the tip. That single gradient does the work a hundred times
     the geometry would: it reads as self-shadowing inside a dense bed, which is exactly what
     is happening down there and exactly what cannot be afforded to compute. */
  vec3 tip = mix(uTipA, uTipB, vTint);
  vec3 base = mix(uRoot, tip, vUp * vUp * 0.80 + vUp * 0.20);

  float ndl = dot(N, L) * 0.5 + 0.5;
  float shade = mix(1.0, shadowAt(vWorldPos, N, dot(N, L)), uShadowStrength);
  vec3 ambient = mix(uAmbientBottom, uAmbientTop, N.y * 0.5 + 0.5);
  vec3 col = base * (ambient + uSunColor * (ndl * ndl) * shade);

  // A blade being pressed is a blade under something, whatever the shadow map caught.
  col *= 1.0 - vPressed * 0.28;

  float fd = uFogDensity * length(cameraPosition - vWorldPos);
  col = mix(col, uFogColor, clamp(1.0 - exp(-fd * fd), 0.0, 1.0));

  /* Bioluminescence, added AFTER the fog. A light source is not attenuated toward the fog
     colour the way a lit surface is — it is the thing making the light — so mixing it in
     before would turn every distant glow the colour of the water. It is still damped by
     range, just far less than the surface under it. */
  if (uNight > 0.01) {
    /* Parts of the bed are livelier than others, and they drift: a uniformly glowing meadow
       reads as an emissive texture rather than as something alive.

       Called 'bloom' because 'patch' is a RESERVED WORD in GLSL ES - a tessellation qualifier
       - and naming a float after it kills the whole shader. Second time this exact class of
       bug has taken this file down; see tools/glslcheck.mjs. */
    float bloom = 0.42 + 0.58 * (0.5 + 0.5 * sin(dot(vWorldPos.xz, vec2(0.29, 0.21))
                                                 - uTime * 0.23 + vTint * 5.4));
    float rest = 0.10 * bloom;
    // Disturbance is the real trigger, and it is squared so a brush is a shimmer and a shark
    // going through at speed is a flare.
    float lit = vPressed * vPressed * 2.6;
    float bio = (rest + lit) * uNight * (0.30 + 0.70 * vUp);
    col += uGlow * bio * (1.0 - clamp(1.0 - exp(-fd * fd), 0.0, 1.0) * 0.55);
  }

  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** how thickly algae grows at a spot: 0 none, 1 full bed */
export function algaeDensity(x: number, z: number): number {
  const d = waterDepth(x, z);
  /* Deeper than the old seagrass band. Kelp this tall standing in four units of water would
     be lying on the surface; it wants the lagoon proper and the foot of the back-reef. */
  const band = smooth(6.0, 9.5, d) * (1 - smooth(20.0, 27.0, d));
  // It roots in sediment, so rock excludes it.
  const sand = 1 - Math.min(1, reefMask(x, z) * 1.6);
  // Patchy at 55-150 u, which is the scale that reads as "a meadow" rather than "grass".
  const patch =
    Math.sin(x * 0.081 + 1.3) * 0.55 +
    Math.sin(z * 0.113 - 0.7) * 0.45 +
    Math.sin(x * 0.041 + z * 0.037 + 2.2) * 0.7;
  return band * sand * smooth(-0.25, 0.75, patch);
}

export class Algae {
  readonly mesh: THREE.Mesh;

  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly baseAttr: THREE.InstancedBufferAttribute;
  private readonly bladeAttr: THREE.InstancedBufferAttribute;
  private readonly distPos: THREE.Vector4[] = [];
  private readonly distDrive: THREE.Vector3[] = [];
  private readonly seed: number;
  private cellX = Number.NaN;
  private cellZ = Number.NaN;
  private live = 0;

  constructor(seed = 0xa16ae) {
    this.seed = seed >>> 0;

    /* One blade: LEVELS unit cubes stacked in 0..1, carrying their own height fraction so the
       vertex stage can bend the column without needing to know which cube it is in. Full
       cubes, interior faces and all — a bent column separates at the joints, and a hollow one
       would show daylight through itself exactly where the bend is most visible. */
    const pos: number[] = [];
    const nrm: number[] = [];
    const vv: number[] = [];
    const idx: number[] = [];
    const FACES: Array<[number[], number[]]> = [
      [[0, 0, 1], [-0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5]],
      [[0, 0, -1], [0.5, -0.5, -0.5, -0.5, -0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5]],
      [[1, 0, 0], [0.5, -0.5, 0.5, 0.5, -0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5]],
      [[-1, 0, 0], [-0.5, -0.5, -0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, -0.5]],
      [[0, 1, 0], [-0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5, -0.5, 0.5, -0.5]],
      [[0, -1, 0], [-0.5, -0.5, -0.5, 0.5, -0.5, -0.5, 0.5, -0.5, 0.5, -0.5, -0.5, 0.5]],
    ];
    for (let c = 0; c < LEVELS; c++) {
      const y0 = c / LEVELS;
      const h = 1 / LEVELS;
      const mid = (c + 0.5) / LEVELS;
      for (const [n, quad] of FACES) {
        const b = pos.length / 3;
        for (let k = 0; k < 4; k++) {
          pos.push(quad[k * 3], y0 + (quad[k * 3 + 1] + 0.5) * h, quad[k * 3 + 2]);
          nrm.push(n[0], n[1], n[2]);
          vv.push(mid);
        }
        idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
      }
    }

    this.geometry = new THREE.InstancedBufferGeometry();
    this.geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    this.geometry.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
    this.geometry.setAttribute('aV', new THREE.Float32BufferAttribute(vv, 1));
    this.geometry.setIndex(idx);

    this.baseAttr = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3), 3);
    this.bladeAttr = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4);
    this.baseAttr.setUsage(THREE.DynamicDrawUsage);
    this.bladeAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('aBase', this.baseAttr);
    this.geometry.setAttribute('aBlade', this.bladeAttr);
    this.geometry.instanceCount = 0;

    for (let i = 0; i < MAX_DISTURBERS; i++) {
      this.distPos.push(new THREE.Vector4(0, -9999, 0, 1));
      this.distDrive.push(new THREE.Vector3());
    }

    this.material = new THREE.ShaderMaterial({
      // Named so a compile failure says WHICH shader; three logs the material name and
      // nothing else identifying, and an empty name means reading every shader in the project.
      name: 'Algae',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uFatten: { value: 1 },
        uCentre: { value: new THREE.Vector2() },
        uFadeX: { value: new THREE.Vector2(FADE_X[0], FADE_X[1]) },
        uFadeZ: { value: new THREE.Vector2(FADE_Z[0], FADE_Z[1]) },
        uDist: { value: this.distPos },
        uDistDrive: { value: this.distDrive },
        uSunDir: {
          value: new THREE.Vector3(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize(),
        },
        uSunColor: { value: v3(srgb(WATER.sunColor)) },
        uAmbientTop: { value: v3(srgb(WATER.ambientTop)) },
        uAmbientBottom: { value: v3(srgb(WATER.ambientBottom)) },
        uFogColor: { value: v3(srgb(WATER.fogColor)) },
        uFogDensity: { value: WATER.fogDensity },
        uRoot: { value: v3(srgb(0x16301c)) },
        uTipA: { value: v3(srgb(0x44712f)) },
        uTipB: { value: v3(srgb(0x7d9c44)) },
        uNight: { value: 0 },
        // Dinoflagellate blue-green. Pushed past 1 on purpose: this is emitted light going
        // into an ACES curve, and anything that peaks at white lands as pale grey.
        uGlow: { value: new THREE.Vector3(0.22, 1.55, 1.70) },
        uShadowMap: { value: null as THREE.Texture | null },
        uShadowMatrix: { value: new THREE.Matrix4() },
        uShadowStrength: { value: 0 },
      },
      side: THREE.FrontSide,
      fog: false,
      transparent: false,
    });

    /* Casting. The twin shares every uniform BY REFERENCE except uFatten, so it poses with
       the identical sway and the identical disturbance — a shadow of an undisturbed bed under
       a bed that is being parted is worse than no shadow at all. Spreading the uniforms object
       copies the references to each {value} holder rather than the values, which is exactly
       the sharing wanted; only uFatten is replaced with a holder of its own. */
    const castable = this.material as THREE.ShaderMaterial & {
      makeShadowMaterial(): THREE.ShaderMaterial;
    };
    castable.makeShadowMaterial = (): THREE.ShaderMaterial =>
      new THREE.ShaderMaterial({
        name: 'Algae:depth',
        vertexShader: VERT,
        fragmentShader: 'void main() { gl_FragColor = vec4(1.0); }',
        uniforms: { ...this.material.uniforms, uFatten: { value: SHADOW_FATTEN } },
        side: THREE.FrontSide,
        fog: false,
      });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = 'algae';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
  }

  get bladeCount(): number {
    return this.live;
  }

  /**
   * Per frame. Replants only when the player has moved a whole SNAP, so the common case is
   * one uniform write and nothing else.
   */
  update(time: number, px: number, pz: number): void {
    const u = this.material.uniforms;
    u.uTime.value = time;
    // Continuous, every frame, and offset up-screen: this is what the fade is measured from.
    const zc = pz + LOOK_AHEAD;
    (u.uCentre.value as THREE.Vector2).set(px, zc);
    const cx = Math.round(px / SNAP);
    const cz = Math.round(zc / SNAP);
    if (cx !== this.cellX || cz !== this.cellZ) {
      this.cellX = cx;
      this.cellZ = cz;
      this.plant(cx * SNAP, cz * SNAP);
    }
  }

  /** xorshift on a field, so the per-cell stream costs no allocation inside the plant loop */
  private hs = 0;
  private nextf(): number {
    let h = this.hs;
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    this.hs = h;
    return (h >>> 0) / 4294967296;
  }

  /**
   * Lay out the bed around a snapped centre.
   *
   * Every blade's position, height, yaw and tint come from a hash of its GRID CELL, never from
   * its index in the buffer. That is what makes the bed stationary: the window slides, blades
   * enter and leave the array, and each one is planted in exactly the spot it was before.
   *
   * Two things keep this off the frame budget. Density is sampled once per BLOCK rather than
   * per cell, because the field cannot vary meaningfully over four units. And the bed height
   * is sampled at the block's corners and interpolated, which is exact enough here for a
   * reason worth stating: algae only grows on sand, and the reef rugosity — the only term with
   * a wavelength short enough to matter — is gated off wherever there is sand.
   */
  private plant(cx: number, cz: number): void {
    const base = this.baseAttr.array as Float32Array;
    const blade = this.bladeAttr.array as Float32Array;
    const per = Math.max(1, Math.round(BLOCK / SPACING));
    let n = 0;

    /* Blocks are indexed on an ABSOLUTE world lattice, never relative to the window.
       This is the whole reason the bed is stationary. Iterating `cx + jx * BLOCK` looks
       equivalent, but the snap and the block size do not divide each other, so every replant
       lands the block grid on a different offset — which changes the hash of every cell and
       replants the entire bed somewhere new. That is a bed that visibly repopulates as you
       swim, and it is invisible to any test that moves the window by a common multiple of the
       two. */
    const bx0i = Math.floor((cx - WIN_X * 0.5) / BLOCK);
    const bx1i = Math.ceil((cx + WIN_X * 0.5) / BLOCK);
    const bz0i = Math.floor((cz - WIN_Z * 0.5) / BLOCK);
    const bz1i = Math.ceil((cz + WIN_Z * 0.5) / BLOCK);

    for (let bzi = bz0i; bzi <= bz1i && n < CAP; bzi++) {
      for (let bxi = bx0i; bxi <= bx1i && n < CAP; bxi++) {
        const bx0 = bxi * BLOCK;
        const bz0 = bzi * BLOCK;
        const mx = bx0 + BLOCK * 0.5;
        const mz = bz0 + BLOCK * 0.5;

        /* Trapezoid cull, in player-relative cross-shore units. Everything outside is ground
           the camera cannot reach, whatever the rectangle says. */
        const u = mz - (cz - LOOK_AHEAD);
        if (u < VIEW.near - VIEW_MARGIN || u > VIEW.far + VIEW_MARGIN) continue;
        const t = Math.max(0, Math.min(1, (u - VIEW.near) / (VIEW.far - VIEW.near)));
        if (Math.abs(mx - cx) > VIEW.wideNear + (VIEW.wideFar - VIEW.wideNear) * t + VIEW_MARGIN) continue;

        const d = algaeDensity(mx, mz);
        if (d < 0.06) continue;

        /* NO RANGE THINNING. It is the obvious optimisation and it cannot work here.
           Whether a stipe exists must depend on WORLD POSITION ALONE: the moment it also
           depends on how far away the viewer is, the threshold moves as you swim and stipes
           blink in and out of existence — measured at seven hundred per replant, which is the
           same "despawning" the fade bug produced, arriving by a different route.
           Density is controlled by SPACING instead, which is view-independent by construction. */

        const h00 = bedHeight(bx0, bz0);
        const h10 = bedHeight(bx0 + BLOCK, bz0);
        const h01 = bedHeight(bx0, bz0 + BLOCK);
        const h11 = bedHeight(bx0 + BLOCK, bz0 + BLOCK);

        for (let iz = 0; iz < per && n < CAP; iz++) {
          for (let ix = 0; ix < per && n < CAP; ix++) {
            // Cell identity is the ABSOLUTE cell index, so a blade's hash is a property of the
            // seabed and not of when it happened to be planted.
            this.hs = hashInts(this.seed, hashInts(bxi * 71 + ix, bzi * 131 + iz));

            if (this.nextf() > d) continue;

            const fx = (ix + 0.5) / per;
            const fz = (iz + 0.5) / per;
            const x = bx0 + (fx + (this.nextf() - 0.5) * 0.9 / per) * BLOCK;
            const z = bz0 + (fz + (this.nextf() - 0.5) * 0.9 / per) * BLOCK;

            const u = (x - bx0) / BLOCK;
            const v = (z - bz0) / BLOCK;
            const y = (h00 * (1 - u) + h10 * u) * (1 - v) + (h01 * (1 - u) + h11 * u) * v;

            /* Height. Tall enough to swim INTO — the shark cruises three to eight units off
               the bed, so anything under two is scenery you fly over.

               Then clamped against the actual water column, which does two things at once: it
               stops a stipe poking through the sea surface, and it makes the forest short at
               the edges of the bed and full height in the deep middle, for free and for the
               right reason. */
            const want = (2.6 + this.nextf() * 4.4) * (0.55 + 0.45 * d);
            const room = waterDepth(x, z) - 1.8;

            base[n * 3] = x;
            base[n * 3 + 1] = y - 0.05;
            base[n * 3 + 2] = z;
            blade[n * 4] = this.nextf() * Math.PI;
            blade[n * 4 + 1] = Math.max(0.4, Math.min(want, room));
            blade[n * 4 + 2] = this.nextf() * Math.PI * 2;
            blade[n * 4 + 3] = this.nextf();
            n++;
          }
        }
      }
    }

    this.live = n;
    this.geometry.instanceCount = n;
    this.baseAttr.needsUpdate = true;
    this.bladeAttr.needsUpdate = true;
  }

  /**
   * Hand over the creatures. Callers push a trail — the same creature at several recent
   * moments, weakest last — and the shader takes the strongest hit per blade.
   */
  setDisturbers(list: Disturber[]): void {
    const n = Math.min(list.length, MAX_DISTURBERS);
    for (let i = 0; i < n; i++) {
      const d = list[i];
      this.distPos[i].set(d.x, d.y, d.z, d.radius);
      this.distDrive[i].set(d.amount, d.hx, d.hz);
    }
    for (let i = n; i < MAX_DISTURBERS; i++) this.distDrive[i].x = 0;
  }


  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.material.uniforms, env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    const u = this.material.uniforms;
    u.uShadowMap.value = map;
    (u.uShadowMatrix.value as THREE.Matrix4).copy(matrix);
    u.uShadowStrength.value = map === null ? 0 : strength;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
